import type { IAppRegion } from '~/lib/appSettings';
import type {
  ApiClient,
  IApiClientAuthorization,
  IApiJsonRequest,
} from '../../ApiClient';
import type {
  IApiAuthorizeResponseBody,
  IApiChangePasswordRequestBody,
  IApiChangePasswordResponseBody,
  IApiLogInAsRequestBody,
  IApiLogInAsResponseBody,
  IApiLogInRequestBody,
  IApiLogInResponseBody,
  IApiLogOutResponseBody,
  IApiRequestPasswordChangeRequestBody,
  IApiRequestPasswordChangeResponseBody,
  IApiSignUpRequestBody,
  IApiSignUpResponseBody,
} from './AuthApi.types';

export type IApiRequestOptions = {
  authorization?: IApiClientAuthorization;
};

export class AuthApi {
  public constructor(private client: ApiClient) {}

  public async logIn(
    region: IAppRegion,
    body: IApiLogInRequestBody,
  ): Promise<IApiLogInResponseBody> {
    const request: IApiJsonRequest<IApiLogInRequestBody> = {
      method: 'post',
      path: '/auth/login',
      body,
    };

    const response = await this.client.fetchJson<IApiLogInResponseBody>(
      request,
      { skipAuth: true, region },
    );

    return response.body;
  }

  public async logInAs(
    body: IApiLogInAsRequestBody,
  ): Promise<IApiLogInAsResponseBody> {
    const request: IApiJsonRequest<IApiLogInAsRequestBody> = {
      method: 'post',
      path: '/auth/openSessionAs',
      body,
    };

    const response =
      await this.client.fetchJson<IApiLogInAsResponseBody>(request);

    return response.body;
  }

  public async authorize(): Promise<IApiAuthorizeResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: '/authorize',
    };

    const response =
      await this.client.fetchJson<IApiAuthorizeResponseBody>(request);

    return response.body;
  }

  public async logOut(
    options?: IApiRequestOptions,
  ): Promise<IApiLogOutResponseBody | undefined> {
    const authorization =
      options?.authorization ?? this.client.getAuthorization();
    if (!authorization) {
      return undefined;
    }

    const request: IApiJsonRequest = {
      method: 'post',
      path: '/auth/logout',
      headers: {
        'x-user-id': authorization.user._id,
        'x-auth-token': authorization.authToken,
      },
    };

    const response = await this.client.fetchJson<IApiLogOutResponseBody>(
      request,
      { skipAuth: true, region: authorization.region },
    );

    this.client.clearAuthorization();

    return response.body;
  }

  public async signUp(
    region: IAppRegion,
    body: IApiSignUpRequestBody,
  ): Promise<IApiSignUpResponseBody> {
    const request: IApiJsonRequest<IApiSignUpRequestBody> = {
      method: 'post',
      path: '/admin/auth/register',
      body,
    };

    const response = await this.client.fetchJson<IApiSignUpResponseBody>(
      request,
      { skipAuth: true, region },
    );

    return response.body;
  }

  public async requestPasswordChange(
    region: IAppRegion,
    body: IApiRequestPasswordChangeRequestBody,
  ): Promise<IApiRequestPasswordChangeResponseBody> {
    const request: IApiJsonRequest<IApiRequestPasswordChangeRequestBody> = {
      method: 'post',
      path: '/auth/forgotPassword',
      body,
    };

    const response =
      await this.client.fetchJson<IApiRequestPasswordChangeResponseBody>(
        request,
        { skipAuth: true, region },
      );

    return response.body;
  }

  public async changePassword(
    region: IAppRegion,
    body: IApiChangePasswordRequestBody,
  ): Promise<IApiChangePasswordResponseBody> {
    const request: IApiJsonRequest<IApiChangePasswordRequestBody> = {
      method: 'post',
      path: '/auth/resetPassword',
      body,
    };

    const response =
      await this.client.fetchJson<IApiChangePasswordResponseBody>(request, {
        skipAuth: true,
        region,
      });

    return response.body;
  }
}
